import { t } from "@lingui/macro";
import { FlashList, ListRenderItemInfo } from "@shopify/flash-list";
import graphql from "babel-plugin-relay/macro";
import React, {
  ComponentType,
  JSXElementConstructor,
  ReactElement,
  useCallback,
  useMemo,
} from "react";
import { Platform } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Box, Text } from "@whoosh/design-system/src/components/primitive";

import { Patron } from "../../types";
import { PatronsListItem } from "../PatronsListItem";
import { retainSelectedPatrons } from "../retainSelectedPatrons";
import { MemberListQuery } from "./__generated__/MemberListQuery.graphql";

type iMemberListProps = {
  onCheckPlayer: (player: Patron, checked: boolean) => void;
  onRemovePlayer?: (playerId: string) => void;
  checkNewDisabled?: boolean;
  ListHeaderComponent?:
    | ComponentType<any>
    | ReactElement<any, string | JSXElementConstructor<any>>
    | null;
  bg: React.ComponentProps<typeof Box>["bg"];
  players: string[];
  selectedPatrons?: Patron[];
  currentPatron?: Patron;
  eventIdToExcludeParticipantsFromResults?: string;
  memberQueryRef: PreloadedQuery<MemberListQuery>;
  keepItemInListAfterCheck?: boolean;
  selectionAction?: "swap" | "replace";
  disabledPlayerIds?: string[];
};

const MemberListUnMemoized = ({
  onCheckPlayer,
  onRemovePlayer,
  checkNewDisabled,
  ListHeaderComponent,
  bg,
  players,
  selectedPatrons = [],
  currentPatron,
  eventIdToExcludeParticipantsFromResults,
  memberQueryRef,
  keepItemInListAfterCheck = false,
  selectionAction,
  disabledPlayerIds = [],
}: iMemberListProps) => {
  const queryData = usePreloadedQuery<MemberListQuery>(
    MemberListQueryString,
    memberQueryRef
  );

  const memberData =
    queryData.session?.currentClientProfile?.member?.club?.members;

  const allPlayers = useMemo(() => {
    const isCurrentUserInBooking = players.some(
      (player) => currentPatron?.playerId === player
    );

    const shouldRemovePlayers = !keepItemInListAfterCheck;

    const formattedMemberData: Patron[] =
      memberData?.edges
        ?.filter((p) =>
          shouldRemovePlayers ? !players.includes(p?.node?.id ?? "") : true
        )
        .map?.((member) => ({
          playerId: member?.node?.id || "",
          firstName: member?.node?.firstName || "",
          membershipTypeId: member?.node?.membershipType?.id,
          lastName: member?.node?.lastName || "",
          profileImageId: member?.node?.profileImage?.publicId || "",
          transportationType:
            member?.node?.preferredTransportationType ?? undefined,
          isNotEligible:
            !!eventIdToExcludeParticipantsFromResults &&
            !member?.node?.eligibleForEvent,
          type: "Member",
          memberGroups:
            member?.node?.memberGroups?.flatMap((g) => (g?.id ? [g.id] : [])) ??
            [],
          email: member?.node?.email || undefined,
          phoneNumber: member?.node?.phoneNumber?.nationalNumber || undefined,
        })) || [];

    const resultPlayers: Patron[] =
      !isCurrentUserInBooking && currentPatron?.type
        ? [
            {
              ...currentPatron,
              lastName: `${currentPatron?.lastName} (You)`,
            },
            ...formattedMemberData,
          ]
        : [...formattedMemberData];

    return retainSelectedPatrons({
      availablePatrons: resultPlayers,
      selectedPatrons,
      selectedPlayerIds: players,
      shouldRetainSelectedPatrons: keepItemInListAfterCheck,
    });
  }, [
    currentPatron,
    eventIdToExcludeParticipantsFromResults,
    memberData?.edges,
    players,
    selectedPatrons,
    keepItemInListAfterCheck,
  ]);

  const isItemSelectedAndKept = useCallback(
    (id?: string | null) =>
      keepItemInListAfterCheck && (players?.includes(id ?? "") ?? false),
    [keepItemInListAfterCheck, players]
  );

  return (
    <Box bg={bg} flex={1}>
      <FlashList
        showsVerticalScrollIndicator={Platform.OS === "web"}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        data={allPlayers}
        keyExtractor={(item) => `member-item-${item.playerId}`}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }: ListRenderItemInfo<Patron>) => {
          return (
            <PatronsListItem
              key={item.playerId}
              item={item}
              checkNewDisabled={
                disabledPlayerIds.includes(item.playerId ?? "") ||
                (isItemSelectedAndKept(item.playerId)
                  ? false
                  : checkNewDisabled)
              }
              initiallyChecked={
                isItemSelectedAndKept(item.playerId) ? true : false
              }
              handleCheck={onCheckPlayer}
              onRemovePlayer={onRemovePlayer}
              keepItemInListAfterCheck={keepItemInListAfterCheck}
              selectionAction={selectionAction}
            />
          );
        }}
        ListFooterComponent={<Box style={{ marginBottom: 128 }} />}
        ListEmptyComponent={
          <Box
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="60%"
          >
            <Text color="text-secondary" variant="mainBaseMedium">
              {t`No members match search`}
            </Text>
          </Box>
        }
      />
    </Box>
  );
};

export const MemberList = React.memo(MemberListUnMemoized);

export const MemberListQueryString = graphql`
  query MemberListQuery(
    $search: String
    $players: [ID]
    $agendaId: ID
    $eventIdToExcludeParticipantsFromResults: ID
    $slotId: ID!
    $includeTransportationType: Boolean!
  ) {
    session {
      currentClientProfile {
        member {
          club {
            members(
              first: 12
              search: $search
              players: $players
              agendaId: $agendaId
              active: true
              notRegisteredForEventId: $eventIdToExcludeParticipantsFromResults
            ) {
              edges {
                node {
                  id
                  firstName
                  lastName
                  email
                  phoneNumber {
                    nationalNumber
                  }
                  eligibleForEvent(
                    eventId: $eventIdToExcludeParticipantsFromResults
                  )
                  memberGroups {
                    id
                  }
                  membershipType {
                    id
                  }
                  preferredTransportationType(slotId: $slotId)
                    @include(if: $includeTransportationType) {
                    id
                    code
                    customCode
                  }
                  profileImage {
                    # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                    publicId: renderingKey
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
